import React, { useState, useEffect } from 'react';
import { ReservationHistory, House } from '../../services/types';
import { getStatusColor } from '../../utils/reservationHelpers';
import moment from 'moment';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from 'react-bootstrap';
import '../reservations/CalendarView.css';
import '../reservations/MultiMonthCalendarView.css';

interface HistoryCalendarViewProps {
    reservations: ReservationHistory[];
    houses?: House[];
    onSelectReservation: (reservation: ReservationHistory) => void;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MAX_VISIBLE_EVENTS = 15;

const HistoryCalendarView: React.FC<HistoryCalendarViewProps> = ({
    reservations,
    houses = [],
    onSelectReservation,
}) => {
    const [currentMonth, setCurrentMonth] = useState<{ year: number; month: number }>({
        year: moment().year(),
        month: moment().month(),
    });
    const [isMobile, setIsMobile] = useState(false);
    const [expandedWeeks, setExpandedWeeks] = useState<Set<number>>(new Set());
    const [hoveredReservationId, setHoveredReservationId] = useState<string | null>(null);

    // Detect mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 920);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handlePrevMonth = () => {
        setCurrentMonth((prev) => {
            const newDate = moment().year(prev.year).month(prev.month).subtract(1, 'month');
            return { year: newDate.year(), month: newDate.month() };
        });
    };

    const handleNextMonth = () => {
        setCurrentMonth((prev) => {
            const newDate = moment().year(prev.year).month(prev.month).add(1, 'month');
            return { year: newDate.year(), month: newDate.month() };
        });
    };

    const handleToday = () => {
        const today = moment();
        setCurrentMonth({ year: today.year(), month: today.month() });
    };

    const getReservationsForWeek = (week: Date[]) => {
        const weekStart = week[0];
        const weekEnd = week[6];

        return reservations.filter((reservation) => {
            const checkIn = moment(reservation.check_in_date).startOf('day');
            const checkOut = moment(reservation.check_out_date).startOf('day');
            const wStart = moment(weekStart).startOf('day');
            const wEnd = moment(weekEnd).startOf('day');

            return checkIn.isSameOrBefore(wEnd) && checkOut.isSameOrAfter(wStart);
        });
    };

    const getReservationPosition = (reservation: ReservationHistory, week: Date[]) => {
        const checkIn = moment(reservation.check_in_date).startOf('day');
        const checkOut = moment(reservation.check_out_date).startOf('day');
        const weekStart = moment(week[0]).startOf('day');
        const weekEnd = moment(week[6]).startOf('day');

        const startsBeforeWeek = checkIn.isBefore(weekStart);
        const endsAfterWeek = checkOut.isAfter(weekEnd);

        let start = 0;
        for (let i = 0; i < 7; i++) {
            if (moment(week[i]).isSameOrAfter(checkIn)) {
                start = startsBeforeWeek ? i : i + 0.5;
                break;
            }
        }

        const displayStart = moment.max(checkIn, weekStart);
        const displayEnd = moment.min(checkOut, weekEnd);
        let span = displayEnd.diff(displayStart, 'days');

        span += 0.5;

        return { start, span: Math.max(0.5, span) };
    };

    const assignReservationsToRows = (reservations: ReservationHistory[], week: Date[]) => {
        const reservationsWithRows: Array<{ reservation: ReservationHistory; row: number }> = [];

        const sorted = [...reservations].sort((a, b) => {
            const aStart = moment(a.check_in_date).valueOf();
            const bStart = moment(b.check_in_date).valueOf();
            if (aStart !== bStart) return aStart - bStart;

            const aDuration = moment(a.check_out_date).diff(moment(a.check_in_date));
            const bDuration = moment(b.check_out_date).diff(moment(b.check_in_date));
            return bDuration - aDuration;
        });

        sorted.forEach(reservation => {
            const { start, span } = getReservationPosition(reservation, week);
            const end = start + span;

            let row = 0;
            while (true) {
                const conflict = reservationsWithRows.find(r => {
                    if (r.row !== row) return false;
                    const otherPos = getReservationPosition(r.reservation, week);
                    const otherEnd = otherPos.start + otherPos.span;
                    return !(end <= otherPos.start || start >= otherEnd);
                });

                if (!conflict) {
                    reservationsWithRows.push({ reservation, row });
                    break;
                }
                row++;
            }
        });

        return reservationsWithRows;
    };

    const getHouseName = (reservation: ReservationHistory): string => {
        if (reservation.house_name) return reservation.house_name;
        if (reservation.house_id && houses.length > 0) {
            const house = houses.find(h => h.public_id === reservation.house_id);
            return house ? (house.name || house.address) : 'House';
        }
        return 'House';
    };

    const renderMonth = () => {
        const { year, month } = currentMonth;
        const firstDay = moment().year(year).month(month).startOf('month');
        const lastDay = moment().year(year).month(month).endOf('month');
        const startDate = moment(firstDay).startOf('week');
        const endDate = moment(lastDay).endOf('week');

        const weeks: Date[][] = [];
        let currentWeek: Date[] = [];
        let currentDate = moment(startDate);

        while (currentDate.isSameOrBefore(endDate)) {
            currentWeek.push(currentDate.toDate());

            if (currentWeek.length === 7) {
                weeks.push(currentWeek);
                currentWeek = [];
            }

            currentDate.add(1, 'day');
        }

        return (
            <div className="multi-month-calendar-month">
                <div className="month-header">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={handlePrevMonth}
                            className="btn btn-outline-primary btn-sm"
                        >
                            <ChevronLeft size={20} />
                        </Button>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h5 style={{ margin: 0 }}>{firstDay.format('MMMM YYYY')}</h5>
                            <button
                                onClick={handleToday}
                                style={{
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    background: '#f0f0f0',
                                    padding: '5px 12px',
                                    cursor: 'pointer',
                                    fontWeight: 'normal',
                                    fontSize: '0.9rem',
                                    transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#e0e0e0')}
                                onMouseLeave={e => (e.currentTarget.style.background = '#f0f0f0')}
                            >
                                Today
                            </button>
                        </div>

                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={handleNextMonth}
                            className="btn btn-outline-primary btn-sm"
                        >
                            <ChevronRight size={20} />
                        </Button>
                    </div>
                </div>

                <div className="weekdays-header">
                    {WEEKDAYS.map((day) => (
                        <div key={day} className="weekday">
                            {day}
                        </div>
                    ))}
                </div>

                <div className="weeks-container">
                    {weeks.map((week, weekIndex) => {
                        const weekReservations = getReservationsForWeek(week);
                        const reservationsWithRows = assignReservationsToRows(weekReservations, week);

                        const maxRow = Math.max(0, ...reservationsWithRows.map(r => r.row));
                        const visibleRows = Math.min(maxRow + 1, MAX_VISIBLE_EVENTS);

                        const reservationHeight = 22;
                        const reservationGap = 0.5;

                        const isCrowded = maxRow >= 3;
                        const baseHeight = isCrowded ? 120 : 100;
                        const firstReservationTop = isCrowded ? 36 : 28;
                        const dynamicMinHeight = Math.max(baseHeight, firstReservationTop + (visibleRows * (reservationHeight + reservationGap)) + 10);

                        const totalHiddenReservations = reservationsWithRows.filter(({ row }) => row >= MAX_VISIBLE_EVENTS).length;
                        const hasMoreInWeek = totalHiddenReservations > 0;

                        const lastVisibleRow = Math.min(maxRow, MAX_VISIBLE_EVENTS - 1);
                        const moreIndicatorTop = firstReservationTop + ((lastVisibleRow + 1) * (reservationHeight + reservationGap)) + 0.5;

                        return (
                            <div
                                key={weekIndex}
                                className="week-wrapper"
                                style={{ minHeight: `${dynamicMinHeight}px` }}
                            >
                                <div className="week-row">
                                    {week.map((date) => {
                                        const isCurrentMonth = moment(date).month() === month;
                                        const isToday = moment(date).isSame(moment(), 'day');
                                        const isPast = moment(date).isBefore(moment(), 'day');

                                        if (!isCurrentMonth) {
                                            return (
                                                <div
                                                    key={date.toISOString()}
                                                    className="calendar-day empty"
                                                >
                                                </div>
                                            );
                                        }

                                        return (
                                            <div
                                                key={date.toISOString()}
                                                className={`
                                                    calendar-day
                                                    ${isToday ? 'today' : ''}
                                                    ${isPast ? 'past' : ''}
                                                    disabled-selection
                                                    ${isCrowded ? 'crowded-week' : ''}
                                                `}
                                                data-date={date.toISOString()}
                                                style={{
                                                    cursor: 'default',
                                                    minHeight: `${dynamicMinHeight}px`,
                                                    padding: isCrowded ? '10px' : '6px'
                                                }}
                                            >
                                                <div className="day-number">
                                                    {moment(date).date()}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {hasMoreInWeek && !expandedWeeks.has(weekIndex) && (
                                    <div
                                        className="show-more-link week-show-more"
                                        style={{
                                            top: `${moreIndicatorTop}px`,
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedWeeks(prev => {
                                                const newSet = new Set(prev);
                                                newSet.add(weekIndex);
                                                return newSet;
                                            });
                                        }}
                                    >
                                        +{totalHiddenReservations} more
                                    </div>
                                )}

                                <div className="week-reservations">
                                    {reservationsWithRows
                                        .filter(({ row }) => row < MAX_VISIBLE_EVENTS)
                                        .map(({ reservation, row }) => {
                                            const { start, span } = getReservationPosition(reservation, week);
                                            const colors = getStatusColor(reservation.reservation_state);
                                            const houseName = getHouseName(reservation);

                                            const topPosition = firstReservationTop + (row * (reservationHeight + reservationGap));

                                            const gapWidth = isMobile ? 2 : 4;
                                            const totalGapWidth = 6 * gapWidth;
                                            const columnWidth = `calc((100% - ${totalGapWidth}px) / 7)`;
                                            const leftPosition = `calc(${columnWidth} * ${start} + ${gapWidth * start}px)`;
                                            const widthCalc = `calc(${columnWidth} * ${span} + ${gapWidth * (span - 1)}px)`;

                                            const isHovered = hoveredReservationId === reservation.public_id;

                                            return (
                                                <div
                                                    key={reservation.public_id}
                                                    className={`reservation-bar ${isHovered ? 'reservation-bar-hovered' : ''}`}
                                                    style={{
                                                        backgroundColor: colors.bg,
                                                        borderLeft: `4px solid ${colors.border}`,
                                                        color: colors.text,
                                                        top: `${topPosition}px`,
                                                        left: leftPosition,
                                                        width: widthCalc,
                                                    }}
                                                    onMouseEnter={() => setHoveredReservationId(reservation.public_id)}
                                                    onMouseLeave={() => setHoveredReservationId(null)}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onSelectReservation(reservation);
                                                    }}
                                                    title={`${reservation.name_first_guess} - ${houseName}`}
                                                >
                                                    <span className="reservation-bar-content">
                                                        {reservation.name_first_guess || 'Guest'} - {houseName}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                </div>

                                {expandedWeeks.has(weekIndex) && maxRow >= MAX_VISIBLE_EVENTS && (
                                    <div className="expanded-reservations">
                                        <div className="expanded-reservations-header">
                                            <span>Additional Reservations</span>
                                            <button
                                                className="expanded-close-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpandedWeeks(prev => {
                                                        const newSet = new Set(prev);
                                                        newSet.delete(weekIndex);
                                                        return newSet;
                                                    });
                                                }}
                                                title="Close"
                                            >
                                                <X size={18} />
                                            </button>
                                        </div>
                                        <div className="expanded-reservations-list">
                                            {reservationsWithRows
                                                .filter(({ row }) => row >= MAX_VISIBLE_EVENTS)
                                                .map(({ reservation }) => {
                                                    const colors = getStatusColor(reservation.reservation_state);
                                                    const houseName = getHouseName(reservation);
                                                    const checkIn = moment(reservation.check_in_date).format('MMM DD');
                                                    const checkOut = moment(reservation.check_out_date).format('MMM DD');

                                                    const isHovered = hoveredReservationId === reservation.public_id;

                                                    return (
                                                        <div
                                                            key={reservation.public_id}
                                                            className={`expanded-reservation-item ${isHovered ? 'reservation-bar-hovered' : ''}`}
                                                            style={{
                                                                backgroundColor: colors.bg,
                                                                borderLeft: `4px solid ${colors.border}`,
                                                                color: colors.text,
                                                            }}
                                                            onMouseEnter={() => setHoveredReservationId(reservation.public_id)}
                                                            onMouseLeave={() => setHoveredReservationId(null)}
                                                            onClick={() => onSelectReservation(reservation)}
                                                        >
                                                            <div className="expanded-reservation-name">
                                                                {reservation.name_first_guess || 'Guest'}
                                                            </div>
                                                            <div className="expanded-reservation-details">
                                                                <span>{houseName}</span>
                                                                <span className="expanded-reservation-dates">
                                                                    {checkIn} → {checkOut}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="calendar-view-container" style={{ height: '100%' }}>
            <div className="multi-month-calendar-container">
                {renderMonth()}
            </div>
        </div>
    );
};

export default HistoryCalendarView;
